import { useCallback, useState } from 'react';

import { nanoid } from 'nanoid';

import { useA2AClient } from '@cast/features-organization-shared-opspilot';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import type {
  AutopilotRecommendation,
  EnvironmentType,
  OptimizationGoal,
} from '../open-api-types/autopilot-types';

type GenerateParams = {
  clusterName: string;
  priority: OptimizationGoal;
  environmentType: EnvironmentType;
  excludedNamespaces: string[];
};

const PRIORITY_TO_GOAL: Record<OptimizationGoal, string> = {
  COST_SAVING: 'cost',
  BALANCED: 'balanced',
  PERFORMANCE: 'performance',
};

const buildPrompt = (params: GenerateParams): string => {
  const { clusterName, priority, environmentType, excludedNamespaces } = params;
  const lines = [
    `Generate workload autoscaling policies for cluster ${clusterName}`,
    '',
    `Environment Type: ${environmentType}`,
    `Goal: ${PRIORITY_TO_GOAL[priority]}`,
  ];
  if (excludedNamespaces.length > 0) {
    lines.push(`Exclude: ${excludedNamespaces.join(',')}`);
  }
  return lines.join('\n');
};

const isAutopilotRecommendation = (
  value: unknown
): value is AutopilotRecommendation =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'autopilotRecommendation';

const parseResponse = (
  parts: { kind: string; data?: unknown; text?: string }[]
): AutopilotRecommendation => {
  for (const part of parts) {
    if (part.kind === 'data' && isAutopilotRecommendation(part.data)) {
      return part.data;
    }
  }
  for (const part of parts) {
    if (part.kind === 'text' && part.text) {
      try {
        const parsed: unknown = JSON.parse(part.text);
        if (isAutopilotRecommendation(parsed)) {
          return parsed;
        }
      } catch {
        // skip non-JSON text parts
      }
    }
  }
  throw new Error('Agent response did not contain a recommendation');
};

const MAX_POLL_DURATION_MS = 300_000;
const MAX_CONSECUTIVE_ERRORS = 3;

const getPollInterval = (elapsedMs: number): number => {
  if (elapsedMs < 120_000) return 10_000;
  if (elapsedMs < 180_000) return 5_000;
  return 3_000;
};

const abortableDelay = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });

export const useAutoPilotA2A = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { currentOrganization } = useOrganizations();
  const { clientRef, startNewController, abortInFlight } = useA2AClient((msg) =>
    setError(msg)
  );

  const generate = useCallback(
    async (params: GenerateParams): Promise<AutopilotRecommendation> => {
      setError(null);
      setLoading(true);
      const controller = startNewController();

      try {
        const client = clientRef.current;
        if (!client) {
          throw new Error(
            'AutoPilot agent is not ready yet. Please try again.'
          );
        }

        const prompt = buildPrompt(params);

        const res = await client.sendMessage({
          message: {
            contextId: nanoid(),
            kind: 'message',
            messageId: nanoid(),
            role: 'user',
            parts: [{ kind: 'text', text: prompt }],
            metadata: {
              org_id: currentOrganization?.id,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          },
          configuration: { blocking: false },
        });

        if ('error' in res) {
          throw new Error('A2A request returned an error');
        }

        const { result } = res;

        if (result.kind !== 'task') {
          throw new Error('Expected a task response from the agent');
        }

        const taskId = result.id;
        const startTime = Date.now();
        let consecutiveErrors = 0;

        while (Date.now() - startTime < MAX_POLL_DURATION_MS) {
          const elapsed = Date.now() - startTime;
          await abortableDelay(getPollInterval(elapsed), controller.signal);

          let taskRes;
          try {
            taskRes = await client.getTask({ id: taskId });
            consecutiveErrors = 0;
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              throw err;
            }
            consecutiveErrors++;
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) throw err;
            continue;
          }

          if ('error' in taskRes) {
            throw new Error('Failed to check task status');
          }

          const task = taskRes.result;

          if (task.status.state === 'completed') {
            const allParts = (task.artifacts ?? []).flatMap((a) => a.parts);
            return parseResponse(allParts);
          }

          if (
            task.status.state === 'failed' ||
            task.status.state === 'canceled'
          ) {
            const firstPart = task.status.message?.parts[0];
            const msg = firstPart?.kind === 'text' ? firstPart.text : undefined;
            throw new Error(msg || 'Task failed');
          }
        }

        throw new Error('Generation timed out. Please try again.');
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to generate recommendations. Please try again.';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [clientRef, currentOrganization?.id, startNewController]
  );

  const abort = useCallback(() => {
    abortInFlight();
    setLoading(false);
  }, [abortInFlight]);

  const clearError = useCallback(() => setError(null), []);

  return { generate, abort, clearError, loading, error };
};
